import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { SlidersHorizontal, Play, RefreshCw, Save, Plus, Trash2, Search, AlertTriangle, CheckCircle } from 'lucide-react';
import { attributeAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const GREEN = '#1B5C3A';
const GOLD = '#F5A623';
const PURPLE = '#4C1D95';
const RED = '#dc2626';
const CREAM = '#FAF9F6';
const CHAR = '#1a1a1a';
const MUTED = '#6b7280';
const BORDER = '#e5e7eb';
const IN = { padding: '7px 10px', borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13, fontFamily: 'var(--font-sans)', boxSizing: 'border-box' };
const TYPES = ['select', 'multiselect', 'number', 'text', 'boolean'];

function ToggleSwitch({ checked, onChange }) {
  return (
    <div
      onClick={() => onChange(!checked)}
      style={{
        width: 34,
        height: 18,
        borderRadius: 9,
        background: checked ? '#1B5C3A' : '#ccc',
        position: 'relative',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
        display: 'inline-block',
        verticalAlign: 'middle',
      }}
    >
      <div
        style={{
          width: 14,
          height: 14,
          borderRadius: '50%',
          background: '#fff',
          position: 'absolute',
          top: 2,
          left: checked ? 18 : 2,
          transition: 'left 0.2s',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }}
      />
    </div>
  );
}

export default function CatalogAttributes() {
  useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [openKey, setOpenKey] = useState('');
  const [draft, setDraft] = useState(null);      // attributes being edited
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const pollRef = useRef(null);

  const load = useCallback(() => attributeAPI.adminList()
    .then(r => setData(r.data.data || r.data))
    .catch(e => setErr(e.response?.data?.message || 'Failed to load')), []);
  useEffect(() => { load(); }, [load]);

  const running = data?.status?.running;
  useEffect(() => {
    if (running && !pollRef.current) pollRef.current = setInterval(load, 3000);
    else if (!running && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [running, load]);

  const schemas = data?.schemas || [];
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return schemas.filter(x => !s || `${x.parentName} ${x.name}`.toLowerCase().includes(s));
  }, [schemas, q]);

  const generate = (onlyMissing) => {
    setBusy('gen'); setMsg(''); setErr('');
    attributeAPI.generate(onlyMissing)
      .then(r => { setMsg(r.data.message || 'Started.'); setTimeout(load, 1500); })
      .catch(e => setErr(e.response?.data?.message || 'Could not start'))
      .finally(() => setBusy(''));
  };

  const openEditor = (s) => {
    const k = `${s.scope}:${s.refId}`;
    if (openKey === k) { setOpenKey(''); setDraft(null); return; }
    setOpenKey(k);
    setDraft(JSON.parse(JSON.stringify(s.attributes || [])));
  };
  const setAttr = (i, patch) => setDraft(d => d.map((a, idx) => idx === i ? { ...a, ...patch } : a));
  const addAttr = () => setDraft(d => [...d, { key: '', label: '', type: 'select', options: [], unit: '', required: false, active: true }]);
  const delAttr = (i) => setDraft(d => d.filter((_, idx) => idx !== i));

  const save = (s) => {
    setBusy('save'); setErr('');
    attributeAPI.adminSave(s.scope, s.refId, draft)
      .then(() => { setMsg(`Saved "${s.name}".`); setOpenKey(''); setDraft(null); load(); })
      .catch(e => setErr(e.response?.data?.message || 'Save failed'))
      .finally(() => setBusy(''));
  };

  if (!data && err) return <div style={{ padding: 24 }}><div className="card" style={{ padding: 24, borderLeft: `4px solid ${RED}`, color: RED }}>{err}</div></div>;
  if (!data) return <div style={{ display: 'flex', justifyContent: 'center', minHeight: '60vh', alignItems: 'center', color: MUTED }}>Loading…</div>;

  const st = data.status || {};
  const pct = st.total ? Math.round((st.processed / st.total) * 100) : 0;

  return (
    <div style={{ padding: '32px 40px', background: CREAM, minHeight: '100vh' }}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 32, fontWeight: 800, color: CHAR, margin: 0, letterSpacing: '-0.02em' }}>
          <SlidersHorizontal size={26} style={{ verticalAlign: -4, marginRight: 8, color: GREEN }} />Catalog Attributes
        </h1>
        <p style={{ color: MUTED, fontSize: 15, marginTop: 6, fontWeight: 500, maxWidth: 900, lineHeight: 1.55 }}>
          The specification fields buyers fill when bidding — different per category (phones → RAM, storage, colour; travel → destination, nights, travellers).
          Generate them with AI for every category, then fine-tune any set by hand. Buyers' choices travel with the lead to the vendor.
        </p>
      </div>

      <div className="card" style={{ padding: 20, marginBottom: 18, display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11.5, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 700 }}>Taxonomy nodes</div>
          <div style={{ fontFamily: 'var(--font-serif)', fontSize: 26, fontWeight: 800, color: CHAR }}>{data.totals?.schemas ?? 0}</div>
        </div>
        <div>
          <div style={{ fontSize: 11.5, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 700 }}>With attributes</div>
          <div style={{ fontFamily: 'var(--font-serif)', fontSize: 26, fontWeight: 800, color: GREEN }}>{data.totals?.withAttributes ?? 0}</div>
        </div>
        <div style={{ flex: 1 }} />
        {!data.keyConfigured && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: RED, fontWeight: 700 }}>
            <AlertTriangle size={15} />AI key not configured
          </span>
        )}
        <button onClick={() => generate(true)} disabled={busy === 'gen' || running || !data.keyConfigured} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px', borderRadius: 9, border: `1.5px solid ${PURPLE}`, cursor: 'pointer', background: '#fff', color: PURPLE, fontSize: 13, fontWeight: 700 }}>
          Fill gaps only
        </button>
        <button onClick={() => generate(false)} disabled={busy === 'gen' || running || !data.keyConfigured} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 9, border: 'none', cursor: 'pointer', background: running ? GOLD : GREEN, color: '#fff', fontSize: 13.5, fontWeight: 700, opacity: (!data.keyConfigured) ? 0.5 : 1 }}>
          {running ? <RefreshCw size={15} className="spin" /> : <Play size={15} />}
          {running ? 'Generating…' : 'Generate for all'}
        </button>
      </div>
      <style>{`.spin{animation:spin 1.1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {(running || st.finishedAt) && (
        <div className="card" style={{ padding: 16, marginBottom: 18 }}>
          <div style={{ height: 10, background: '#eef2f0', borderRadius: 5, overflow: 'hidden', marginBottom: 8 }}>
            <div style={{ width: `${pct}%`, height: '100%', background: GREEN, borderRadius: 5, transition: 'width .5s ease' }} />
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13, fontWeight: 600 }}>
            <span style={{ color: CHAR }}>{st.processed}/{st.total} ({pct}%)</span>
            <span style={{ color: GREEN }}>✓ {st.saved} saved</span>
            <span style={{ color: st.failed ? RED : MUTED }}>✗ {st.failed} failed</span>
            {running && st.current && <span style={{ color: PURPLE }}>→ {st.current}</span>}
            {!running && st.finishedAt && <span style={{ color: GREEN, display: 'flex', alignItems: 'center', gap: 5 }}><CheckCircle size={14} />done</span>}
          </div>
          {st.lastError && <div style={{ marginTop: 6, fontSize: 12, color: RED }}>{st.lastError}</div>}
        </div>
      )}

      {msg && <div style={{ padding: '10px 16px', marginBottom: 12, borderRadius: 10, background: 'rgba(27,92,58,0.08)', color: GREEN, fontSize: 13.5, fontWeight: 600 }}>{msg}</div>}
      {err && <div style={{ padding: '10px 16px', marginBottom: 12, borderRadius: 10, background: 'rgba(220,38,38,0.07)', color: RED, fontSize: 13.5, fontWeight: 600 }}>{err}</div>}

      <div style={{ position: 'relative', marginBottom: 14, maxWidth: 340 }}>
        <Search size={15} style={{ position: 'absolute', left: 11, top: 10, color: MUTED }} />
        <input style={{ ...IN, width: '100%', paddingLeft: 34 }} placeholder="Search category / sub-category…" value={q} onChange={e => setQ(e.target.value)} />
      </div>

      {shown.length === 0 ? (
        <div className="card" style={{ padding: '40px 24px', textAlign: 'center', color: MUTED }}>
          {schemas.length === 0 ? 'No attribute sets yet — hit "Generate for all" to build them.' : 'Nothing matches that search.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {shown.map(s => {
            const k = `${s.scope}:${s.refId}`;
            const open = openKey === k;
            return (
              <div key={k} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div onClick={() => openEditor(s)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: 'pointer' }}>
                  <span style={{ fontSize: 10.5, fontWeight: 800, padding: '3px 8px', borderRadius: 8, background: s.scope === 'category' ? 'rgba(27,92,58,0.10)' : s.scope === 'brand' ? 'rgba(245,166,35,0.14)' : 'rgba(76,29,149,0.09)', color: s.scope === 'category' ? GREEN : s.scope === 'brand' ? '#B45309' : PURPLE, textTransform: 'uppercase' }}>
                    {s.scope === 'category' ? 'Category' : s.scope === 'brand' ? 'Brand' : 'Sub'}
                  </span>
                  <span style={{ flex: 1, fontSize: 14, fontWeight: 700, color: CHAR }}>
                    {s.parentName ? <span style={{ color: MUTED, fontWeight: 600 }}>{s.parentName} › </span> : null}{s.name}
                  </span>
                  <span style={{ fontSize: 12, color: (s.attributes || []).length ? GREEN : GOLD, fontWeight: 700 }}>
                    {(s.attributes || []).length || 'none'} {(s.attributes || []).length ? 'fields' : ''}
                  </span>
                  <span style={{ fontSize: 11, color: MUTED }}>{s.source}</span>
                </div>

                {open && draft && (
                  <div style={{ padding: '4px 16px 16px', borderTop: `1px solid ${BORDER}`, background: '#fcfcfb' }}>
                    {draft.map((a, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap', padding: '10px 0', borderBottom: `1px solid ${BORDER}`, opacity: a.active === false ? 0.55 : 1, transition: 'opacity 0.2s' }}>
                        <input style={{ ...IN, width: 130 }} placeholder="label" value={a.label} onChange={e => setAttr(i, { label: e.target.value })} />
                        <input style={{ ...IN, width: 110, fontFamily: 'monospace', fontSize: 12 }} placeholder="key" value={a.key} onChange={e => setAttr(i, { key: e.target.value })} />
                        <select style={{ ...IN, width: 110 }} value={a.type} onChange={e => setAttr(i, { type: e.target.value })}>
                          {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        {(a.type === 'select' || a.type === 'multiselect') && (
                          <input style={{ ...IN, flex: 1, minWidth: 220 }} placeholder="options, comma separated"
                            value={(a.options || []).join(', ')}
                            onChange={e => setAttr(i, { options: e.target.value.split(',').map(x => x.trim()).filter(Boolean) })} />
                        )}
                        {a.type === 'number' && <input style={{ ...IN, width: 90 }} placeholder="unit" value={a.unit} onChange={e => setAttr(i, { unit: e.target.value })} />}
                        <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: MUTED, fontWeight: 600, padding: '7px 0' }}>
                          <input type="checkbox" checked={!!a.required} onChange={e => setAttr(i, { required: e.target.checked })} />required
                        </label>
                        <div title={a.active === false ? "Field Hidden (Click to Enable)" : "Field Visible (Click to Hide)"} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 0', userSelect: 'none' }}>
                          <ToggleSwitch checked={a.active !== false} onChange={val => setAttr(i, { active: val })} />
                          <span style={{ fontSize: 11, fontWeight: 700, color: a.active === false ? RED : GREEN, minWidth: 24 }}>
                            {a.active === false ? 'Off' : 'On'}
                          </span>
                        </div>
                        <button onClick={() => delAttr(i)} title="Remove" style={{ padding: 7, borderRadius: 7, border: `1px solid ${RED}44`, background: '#fff', color: RED, cursor: 'pointer' }}><Trash2 size={13} /></button>
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                      <button onClick={addAttr} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: `1px solid ${BORDER}`, background: '#fff', color: CHAR, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}><Plus size={14} />Add field</button>
                      <button onClick={() => save(s)} disabled={busy === 'save'} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px', borderRadius: 8, border: 'none', background: GREEN, color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}><Save size={14} />Save</button>
                      <button onClick={() => { setOpenKey(''); setDraft(null); }} style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${BORDER}`, background: '#fff', color: MUTED, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
